/**
 * Transport Bar Component (Play/Stop/etc)
 */

import React from "react";
import { usePlaybackStore } from "../store/playbackStore";
import { useRecorderStore } from "../store/recorderStore";
import { useSessionStore } from "../store/sessionStore";
import { AudioEngine } from "../audio/AudioEngine";
import JamroomIconButton from "./JamroomIconButton";
import "./TransportBar.css";

interface TransportBarProps {
  audioEngine: AudioEngine | null;
  totalDurationMs?: number;
}

const TransportBar: React.FC<TransportBarProps> = ({ audioEngine, totalDurationMs = 60000 }) => {
  const { isPlaying, playheadMs, loopEnabled, zoom, setPlaying, setPlayheadMs, setZoom } =
    usePlaybackStore();
  const isRecording = useRecorderStore((s) => s.isRecording);
  const sessionExpired = useSessionStore((s) => s.sessionExpired);

  const seekSeconds = 10;
  const controlsDisabled = !audioEngine || sessionExpired || isRecording;

  const formatTime = (ms: number) => {
    const sec = Math.floor(ms / 1000);
    const min = Math.floor(sec / 60);
    const s = sec % 60;
    const msRemainder = ms % 1000;
    return `${min.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}.${(
      msRemainder / 10
    )
      .toFixed(0)
      .padStart(2, "0")}`;
  };

  const clampMs = (ms: number) => {
    const max = Math.max(0, Math.floor(totalDurationMs));
    const v = Math.max(0, Math.floor(ms));
    return Math.min(max, v);
  };

  const handlePlay = () => {
    if (!audioEngine) return;
    if (controlsDisabled) return;
    if (isPlaying) return;

    void audioEngine.resume().then(() => {
      audioEngine.play(playheadMs);
      setPlaying(true);
    });
  };

  const handlePause = () => {
    if (!audioEngine) return;
    if (controlsDisabled) return;
    if (!isPlaying) return;
    const at = audioEngine.getPlayheadMs();
    audioEngine.stop();
    setPlaying(false);
    setPlayheadMs(at);
  };

  const handleStop = () => {
    if (!audioEngine) return;
    if (controlsDisabled) return;
    setPlaying(false);
    audioEngine.stop();
    setPlayheadMs(0);
  };

  const handleReturn = () => {
    if (controlsDisabled) return;
    if (audioEngine) {
      audioEngine.seek(0);
      setPlayheadMs(0);
    } else {
      setPlayheadMs(0);
    }
  };

  const handleSeekBySeconds = (deltaSeconds: number) => {
    if (!audioEngine) return;
    if (controlsDisabled) return;
    const target = clampMs(playheadMs + deltaSeconds * 1000);
    audioEngine.seek(target);
    setPlayheadMs(target);
  };

  const handleFit = () => {
    const durationSeconds = Math.max(1, totalDurationMs / 1000);
    const usablePixels = Math.max(360, window.innerWidth - 340);
    const fitZoom = Math.floor(usablePixels / durationSeconds);
    const clamped = Math.min(500, Math.max(5, fitZoom));
    setZoom(clamped);
  };

  return (
    <div className="transport-bar">
      <div className="transport-controls">
        <JamroomIconButton
          iconId="jr-restart"
          ariaLabel="Return to start"
          disabled={controlsDisabled}
          onClick={handleReturn}
        />
        <JamroomIconButton
          iconId="jr-rewind-10"
          ariaLabel={`Rewind ${seekSeconds} seconds`}
          disabled={controlsDisabled}
          onClick={() => handleSeekBySeconds(-seekSeconds)}
        />
        <JamroomIconButton
          iconId="jr-play"
          ariaLabel="Play"
          disabled={controlsDisabled || isPlaying}
          onClick={handlePlay}
        />
        <JamroomIconButton
          iconId="jr-pause"
          ariaLabel="Pause"
          disabled={controlsDisabled || !isPlaying}
          onClick={handlePause}
        />
        <JamroomIconButton
          iconId="jr-stop"
          ariaLabel="Stop"
          disabled={controlsDisabled}
          onClick={handleStop}
        />
        <JamroomIconButton
          iconId="jr-forward-10"
          ariaLabel={`Forward ${seekSeconds} seconds`}
          disabled={controlsDisabled}
          onClick={() => handleSeekBySeconds(seekSeconds)}
        />
      </div>

      <div className="time-display">{formatTime(playheadMs)}</div>

      <div className="loop-section">
        <button
          className={`jr-btn jr-btn--ghost loop-btn ${loopEnabled ? "active" : ""}`}
          onClick={() => {
            // TODO: Connect to store
          }}
          title="Toggle loop"
        >
          Loop
        </button>
        <button className="jr-btn jr-btn--ghost fit-btn" onClick={handleFit} title="Fit all audio in view">
          Fit
        </button>
      </div>

      <div className="zoom-section">
        <input
          type="range"
          min="5"
          max="500"
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
          className="zoom-slider"
          title="Zoom timeline"
        />
        <span className="zoom-value">{zoom}px/s</span>
      </div>
    </div>
  );
};

export default TransportBar;
